/**
 * Règles du formulaire de saisie manuelle d'un paiement.
 *
 * PURES, parce que cet écran engage des virements et n'a aucun test d'interface :
 * ce qu'il décide de lire, d'afficher ou d'exiger se teste ici.
 */

export type LectureMontant = { montant: number; erreur: null } | { montant: null; erreur: string | null };

const MONTANT = /^(?:\d+|\d{1,3}(?:\s\d{3})+)(?:[.,]\d{1,2})?$/;

/**
 * Lit un montant saisi en français. `erreur` est null quand rien n'est saisi :
 * un champ vide manque, il n'est pas faux.
 *
 * Une espace n'est acceptée qu'entre groupes de trois chiffres : retirée partout,
 * « 5000 00 » serait lu 500 000 € alors que l'auteur voulait 5 000,00.
 *
 * Un point suivi de trois chiffres est refusé plutôt qu'interprété : « 1.000 »
 * veut dire mille pour qui l'écrit, et 1 € pour un calcul. Sur un virement, on
 * fait préciser au lieu de deviner.
 */
export function lireMontant(saisie: string): LectureMontant {
  const texte = saisie.trim();
  if (texte === '') return { montant: null, erreur: null };
  if (!MONTANT.test(texte)) {
    return {
      montant: null,
      erreur: 'Écrivez le montant en chiffres, avec deux décimales au plus : 5000,00 ou 5 000,00.',
    };
  }
  const montant = Number(texte.replace(/\s/g, '').replace(',', '.'));
  if (montant <= 0) return { montant: null, erreur: 'Le montant doit être supérieur à zéro.' };
  return { montant, erreur: null };
}

function trouver<T extends { vendor_name: string }>(tous: T[], recherche: string): T[] {
  const q = recherche.trim().toLowerCase();
  return q ? tous.filter((b) => b.vendor_name.toLowerCase().includes(q)) : tous;
}

/**
 * La recherche ne trouve personne, bénéficiaire retenu mis à part : celui-ci
 * reste affiché dans la liste, et ne doit pas masquer qu'il faut créer le
 * bénéficiaire cherché.
 */
export function rechercheSansResultat<T extends { vendor_name: string }>(tous: T[], recherche: string): boolean {
  return trouver(tous, recherche).length === 0;
}

/**
 * Les bénéficiaires à montrer dans la liste, triés par nom.
 *
 * Le bénéficiaire retenu y reste même si la recherche l'exclut : sorti de la
 * liste, il resterait enregistré pendant qu'un autre nom y est surligné, et le
 * virement partirait vers un compte que l'écran ne montre plus.
 */
export function beneficiairesAffiches<T extends { id: string; vendor_name: string }>(
  tous: T[],
  recherche: string,
  retenuId: string,
): T[] {
  const tries = [...trouver(tous, recherche)].sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
  const retenu = tous.find((b) => b.id === retenuId);
  return retenu && !tries.includes(retenu) ? [retenu, ...tries] : tries;
}

/**
 * Les avertissements de « À payer » sur les lignes sans coordonnées bancaires.
 *
 * Le remède n'est pas le même : une ligne venant de Books se rattache dès que le
 * bénéficiaire existe, alors qu'une saisie manuelle garde le bénéficiaire choisi
 * et ne se répare qu'en la ressaisissant. Un virement interne suit la même règle.
 */
export function avertissementsCoordonnees(lignes: Array<{ iban_manquant: boolean; origine?: string }>): string[] {
  const sansCoordonnees = lignes.filter((l) => l.iban_manquant);
  const manuelles = sansCoordonnees.filter((l) => l.origine === 'manuel' || l.origine === 'virement_interne').length;
  const autres = sansCoordonnees.length - manuelles;
  const messages: string[] = [];
  if (autres > 0) {
    messages.push(
      `${autres} facture(s) sans coordonnées bancaires (bénéficiaire introuvable) : ajoute le bénéficiaire pour pouvoir les payer.`,
    );
  }
  if (manuelles > 0) {
    messages.push(
      `${manuelles} saisie(s) manuelle(s) sans coordonnées bancaires (bénéficiaire supprimé) : supprime la ligne, puis saisis-la à nouveau.`,
    );
  }
  return messages;
}

/** Ce qu'il reste à remplir, dit à côté du bouton : grisé sans explication, il laissait chercher. */
export function champsManquants(saisie: {
  beneficiaireId: string;
  numero: string;
  montant: string;
  motif: string;
}): string[] {
  const manquants: string[] = [];
  if (saisie.beneficiaireId === '') manquants.push('le bénéficiaire');
  if (saisie.numero.trim() === '') manquants.push('le n° de facture');
  if (lireMontant(saisie.montant).montant === null) manquants.push('un montant valide');
  if (saisie.motif.trim() === '') manquants.push('le motif de la saisie');
  return manquants;
}
